// One bar and one "about N left" for the whole upload. Pure.
//
// Three stretches, weighted roughly by how long they take for a typical song:
//   preparing (convert, mix, previews)  0  → 15%
//   sending (bytes over the wire)       15 → 85%
//   checking (/complete re-hashes every object on the server)  85 → 99%
// The check reports nothing back, so its share creeps on an estimate from the
// bytes sent: one beta run verified ~50 MB in ~8 s.

export type ProgressStage =
  | { kind: "preparing"; fraction: number }
  | { kind: "sending"; sentBytes: number; totalBytes: number; elapsedSec: number }
  | { kind: "checking"; totalBytes: number; elapsedSec: number }
  | { kind: "done" };

/** Seconds per megabyte the server takes to verify, measured on beta. */
const VERIFY_SEC_PER_MB = 0.17;
const MB = 1024 * 1024;

export function verifyEstimateSec(totalBytes: number): number {
  return Math.max(3, (totalBytes / MB) * VERIFY_SEC_PER_MB);
}

/** 0..1 across the whole upload. Never reaches 1 before it's done. */
export function overallProgress(stage: ProgressStage): number {
  switch (stage.kind) {
    case "preparing":
      return 0.15 * clamp01(stage.fraction);
    case "sending":
      return 0.15 + 0.7 * (stage.totalBytes > 0 ? clamp01(stage.sentBytes / stage.totalBytes) : 1);
    case "checking":
      return Math.min(0.99, 0.85 + 0.14 * clamp01(stage.elapsedSec / verifyEstimateSec(stage.totalBytes)));
    case "done":
      return 1;
  }
}

/**
 * Seconds left, or null while there's nothing to go on yet (preparing, or the
 * first couple of seconds of sending before a rate means anything).
 */
export function secondsLeft(stage: ProgressStage): number | null {
  if (stage.kind === "sending") {
    if (stage.elapsedSec < 2 || stage.sentBytes <= 0) return null;
    const rate = stage.sentBytes / stage.elapsedSec;
    return (stage.totalBytes - stage.sentBytes) / rate + verifyEstimateSec(stage.totalBytes);
  }
  if (stage.kind === "checking") return Math.max(0, verifyEstimateSec(stage.totalBytes) - stage.elapsedSec);
  return null;
}

/** "About 2 min left", "About 40 s left", "Almost done". */
export function timeLeftText(seconds: number | null): string | null {
  if (seconds === null) return null;
  if (seconds < 10) return "Almost done";
  if (seconds < 60) return `About ${Math.round(seconds / 10) * 10} s left`;
  return `About ${Math.round(seconds / 60)} min left`;
}

/** How far through preparing a stage is, for the first slice of the bar. */
export function prepareFraction(stage: string, done?: number, total?: number): number {
  switch (stage) {
    case "converting":
      return total ? 0.5 * ((done ?? 0) / total) : 0;
    case "mixing":
      return 0.55;
    case "encoding":
      return 0.7;
    case "opening":
      return 0.95;
    default:
      return 0;
  }
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}
